// row-layer-names.ts — ONE answer to "which root keys of a held row are layers?"
//
// A held row carries its authored layers under `item_layer_types` and, where PHP still descalars them, as legacy
// flat root keys (`ftLayerSlot`'s top-level fallback reads those). Everything else at the root is STRUCTURE:
// row buckets, display identity, and the object-API / browser-surface metadata that travels `_`-prefixed
// (`_meta`, `_pl_stash`, `_entity_base`, `_entity_refusal`, `_inventory_refusal`, `_random_table_inputs`).
// The entity paint keeps every `_` key on a hydrated host (entity-display.ts paintEntitySurface; base-patch.ts
// refuse()), so a definition fetched through object_inc.php does NOT have the key set of one carried in
// encounter_arr's catalog. execrhythm28b (2026-10-28): three private copies of this enumeration each excluded a
// different ad-hoc list; the hydrated Powerful's `_pl_stash` read as an unported status layer and refused the
// Champion's greatsword swing on served bytes, while every offline fixture (no `_pl_stash`) passed.
type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Root keys that are never layers on any held row kind (definition, status, inventory instance, member). */
export const ROW_STRUCTURAL_KEYS: ReadonlySet<string> = new Set(['items', 'item_layer_types', 'computed', 'catalog',
    // identity, media and viewer buckets
    'item_id', 'item_name', 'item_type', 'assets', 'pictures', 'item_picture', 'meshes', 'permissions', 'preview_context',
    // inventory instance / member buckets
    'inventory', 'position', 'inventory_in_container', 'enchantment_templates', 'enchantment_template_enchantments',
    // compiled provenance
    'source', 'built_from', 'item_id_used', 'attack_ability_arr']);

/** Object-API and browser-surface metadata is `_`-prefixed by convention; no authored layer name is. */
export const isRowStructuralKey = (key: string): boolean => key.startsWith('_') || ROW_STRUCTURAL_KEYS.has(key);

/** Bucketed and legacy flat layer NAMES of a held row, structure excluded, each once. The caller reads values
 * through its own page reader; an empty slot is still the caller's call. */
export function rowLayerNames(row: Row): string[] {
    return [...new Set([...Object.keys(record(row.item_layer_types) ?? {}), ...Object.keys(row).filter(key => !isRowStructuralKey(key))])];
}
